(function () {
  'use strict';

  // Manukind Appstle "Subscription & Save" plans (15% off), shared across
  // all subscribable products. Order here = order in the cart dropdown.
  // When plans are added/removed in Appstle, update this list (IDs come
  // from /products/<handle>.js → selling_plan_groups[].selling_plans[].id).
  const SELLING_PLANS = [
    { id: '5567316199', name: 'Delivery every 4 weeks' },
    { id: '6457884903', name: 'Delivery every 5 weeks' },
    { id: '6457917671', name: 'Delivery every 6 weeks' },
    { id: '6457950439', name: 'Delivery every 8 weeks' },
  ];

  const DEFAULT_SELLING_PLAN_ID = '5567316199';
  const PLAN_CACHE_TTL_MS = 30 * 60 * 1000;

  let cartCache = null;
  let injectScheduled = false;
  let isInjectingCustomDrawer = false;

  const lineLocks = new Map();
  const productPlanCache = {};
  const ineligibleVariants = new Set();

  function enqueueCartMutation(fn) {
    if (typeof window._fcdEnqueueCartMutation === 'function') {
      return window._fcdEnqueueCartMutation(fn);
    }
    return Promise.resolve().then(fn);
  }

  async function fetchCart() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Cart fetch failed: ' + res.status);
      cartCache = await res.json();
      window._fcdCart = cartCache;
      return cartCache;
    } catch (err) {
      console.error('[CustomSub] Failed to fetch cart:', err);
      return null;
    }
  }

  async function variantHasSellingPlans(handle, variantId) {
    if (ineligibleVariants.has(String(variantId))) return false;
    const cacheKey = `${handle}_${variantId}`;
    if (productPlanCache[cacheKey] !== undefined) return productPlanCache[cacheKey];

    const stored = sessionStorage.getItem('csub_' + cacheKey);
    if (stored !== null) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === 'object' &&
            typeof parsed.t === 'number' &&
            (Date.now() - parsed.t) < PLAN_CACHE_TTL_MS) {
          productPlanCache[cacheKey] = !!parsed.v;
          return productPlanCache[cacheKey];
        }
      } catch (_) { /* legacy format */ }
    }

    try {
      const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
      const product = await res.json();
      if (!product.selling_plan_groups?.length) {
        productPlanCache[cacheKey] = false;
        sessionStorage.setItem('csub_' + cacheKey, JSON.stringify({ v: false, t: Date.now() }));
        return false;
      }
      const variant = product.variants?.find(v => String(v.id) === String(variantId));
      const hasPlans = variant
        ? Array.isArray(variant.selling_plan_allocations) && variant.selling_plan_allocations.length > 0
        : false;
      productPlanCache[cacheKey] = hasPlans;
      sessionStorage.setItem('csub_' + cacheKey, JSON.stringify({ v: hasPlans, t: Date.now() }));
      return hasPlans;
    } catch (err) {
      productPlanCache[cacheKey] = false;
      return false;
    }
  }

  function ensureSubscriptionBadge(itemEl, planName) {
    if (!itemEl || !planName) return;
    const infoEl = itemEl.querySelector('.fcd-item__info');
    if (!infoEl) return;

    let badge = itemEl.querySelector('.fcd-item__subscription');
    const newText = '♻ ' + planName;

    if (badge) {
      if (badge.textContent !== newText) badge.textContent = newText;
      return;
    }

    badge = document.createElement('p');
    badge.className = 'fcd-item__subscription';
    badge.textContent = newText;

    const variantEl = infoEl.querySelector('.fcd-item__variant');
    const titleEl   = infoEl.querySelector('.fcd-item__title');
    const priceRow  = infoEl.querySelector('.fcd-item__price-row');

    if (variantEl && variantEl.parentNode === infoEl) {
      variantEl.insertAdjacentElement('afterend', badge);
    } else if (titleEl && titleEl.parentNode === infoEl) {
      titleEl.insertAdjacentElement('afterend', badge);
    } else if (priceRow && priceRow.parentNode === infoEl) {
      infoEl.insertBefore(badge, priceRow);
    } else {
      infoEl.appendChild(badge);
    }
  }

  function removeSubscriptionBadge(itemEl) {
    if (!itemEl) return;
    const badge = itemEl.querySelector('.fcd-item__subscription');
    if (badge) badge.remove();
  }

  // Safety net: if the cart line has a plan that isn't in SELLING_PLANS
  // (e.g. a new plan added in Appstle but not here yet), add it to the
  // dropdown so it shows as selected instead of falling back to "One-time".
  function ensurePlanOption(selectEl, planId, planName) {
    if (!selectEl || !planId) return;
    const id = String(planId);
    const exists = Array.prototype.some.call(selectEl.options, o => o.value === id);
    if (exists) return;
    const subGroup = selectEl.querySelector('optgroup[data-csub-group="subscription"]') ||
                     selectEl.querySelector('optgroup:last-of-type');
    if (!subGroup) return;
    const opt = document.createElement('option');
    opt.value = id;
    opt.textContent = planName || 'Subscription';
    subGroup.appendChild(opt);
  }

  function syncSelectToCart(wrapperEl) {
    if (!wrapperEl || !cartCache) return;
    const selectEl = wrapperEl.querySelector('.csub-select');
    if (!selectEl) return;
    const lineKey = wrapperEl.getAttribute('data-line-key');
    if (!lineKey) return;
    const item = cartCache.items.find(i => i.key === lineKey);
    if (!item || !item.selling_plan_allocation) return;
    const realId = String(item.selling_plan_allocation.selling_plan.id);
    ensurePlanOption(selectEl, realId, item.selling_plan_allocation.selling_plan.name);
    if (selectEl.value !== realId) selectEl.value = realId;
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  async function performPlanChange(lineKey, sellingPlanId, selectEl, wrapperEl) {
    if (selectEl) {
      selectEl.disabled = true;
      selectEl.classList.add('csub-select--loading');
    }

    try {
      const preKeys = (cartCache && cartCache.items)
        ? cartCache.items.map(i => i.key)
        : [];

      // Find current quantity to preserve it across the plan change.
      // Some subscription apps reset qty to 1 when adding/changing a plan.
      const currentItem = cartCache && cartCache.items
        ? cartCache.items.find(i => i.key === lineKey)
        : null;
      const preserveQty = currentItem ? currentItem.quantity : undefined;

      const changeBody = {
        id: lineKey,
        selling_plan: sellingPlanId,
        properties: sellingPlanId
          ? { _selling_plan_id: sellingPlanId }
          : {},
      };
      if (preserveQty !== undefined) {
        changeBody.quantity = preserveQty;
      }

      const res = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(changeBody),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        console.error('[CustomSub] Failed to update selling plan:', res.status, errBody);
        syncSelectToCart(wrapperEl);
        return null;
      }

      const fresh = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
      cartCache = fresh;
      window._fcdCart = fresh;

      const itemElForVariant = wrapperEl ? wrapperEl.closest('.fcd-item') : null;
      const variantId = itemElForVariant ? itemElForVariant.dataset.variantId : null;
      const isClearingPlan = !sellingPlanId;

      let updatedItem = null;

      if (isClearingPlan) {
        // Clearing the plan (one-time): find the line that USED to be a sub
        // and is now plain. Match by key first, then by variant.
        updatedItem = fresh.items.find(i => i.key === lineKey && !i.selling_plan_allocation);
        if (!updatedItem) {
          updatedItem = fresh.items.find(i =>
            preKeys.indexOf(i.key) === -1 &&
            !i.selling_plan_allocation &&
            (variantId ? String(i.variant_id) === String(variantId) : true)
          );
        }
        if (!updatedItem && variantId) {
          updatedItem = fresh.items.find(i =>
            !i.selling_plan_allocation &&
            String(i.variant_id) === String(variantId)
          );
        }
      } else {
        // Setting a specific plan: match on selling_plan_allocation
        updatedItem = fresh.items.find(i => i.key === lineKey);
        if (updatedItem && (!updatedItem.selling_plan_allocation ||
            String(updatedItem.selling_plan_allocation.selling_plan.id) !== String(sellingPlanId))) {
          updatedItem = null;
        }

        if (!updatedItem) {
          updatedItem = fresh.items.find(i =>
            preKeys.indexOf(i.key) === -1 &&
            i.selling_plan_allocation &&
            String(i.selling_plan_allocation.selling_plan.id) === String(sellingPlanId) &&
            (variantId ? String(i.variant_id) === String(variantId) : true)
          );
        }

        if (!updatedItem && variantId) {
          updatedItem = fresh.items.find(i =>
            i.selling_plan_allocation &&
            String(i.selling_plan_allocation.selling_plan.id) === String(sellingPlanId) &&
            String(i.variant_id) === String(variantId)
          );
        }
        if (!updatedItem) {
          updatedItem = fresh.items.find(i =>
            i.selling_plan_allocation &&
            String(i.selling_plan_allocation.selling_plan.id) === String(sellingPlanId)
          );
        }
      }

      if (updatedItem && wrapperEl && wrapperEl.isConnected) {
        wrapperEl.setAttribute('data-line-key', updatedItem.key);

        const itemEl = wrapperEl.closest('.fcd-item');
        if (itemEl) {
          itemEl.dataset.key = updatedItem.key;
          itemEl.querySelectorAll('.fcd-qty-btn').forEach(btn => {
            btn.dataset.key = updatedItem.key;
          });
          itemEl.querySelectorAll('.fcd-item__remove').forEach(btn => {
            btn.dataset.key = updatedItem.key;
          });

          if (isClearingPlan) {
            removeSubscriptionBadge(itemEl);
          } else {
            const planName =
              (updatedItem.selling_plan_allocation && updatedItem.selling_plan_allocation.selling_plan.name) ||
              SELLING_PLANS.find(p => p.id === String(sellingPlanId))?.name ||
              '';
            ensureSubscriptionBadge(itemEl, planName);
          }

          const priceEl = itemEl.querySelector('.fcd-item__price');
          if (priceEl) {
            const newPrice = formatMoney(updatedItem.final_line_price);
            if (priceEl.textContent !== newPrice) priceEl.textContent = newPrice;
          }

          const qtyEl = itemEl.querySelector('.fcd-item__qty-num');
          if (qtyEl) {
            const newQty = String(updatedItem.quantity);
            if (qtyEl.textContent !== newQty) qtyEl.textContent = newQty;
          }
          const qtyDecBtn = itemEl.querySelector('.fcd-item__qty .fcd-qty-btn:first-child');
          const qtyIncBtn = itemEl.querySelector('.fcd-item__qty .fcd-qty-btn:last-child');
          if (qtyDecBtn) {
            const dec = String(updatedItem.quantity - 1);
            if (qtyDecBtn.dataset.qty !== dec) qtyDecBtn.dataset.qty = dec;
          }
          if (qtyIncBtn) {
            const inc = String(updatedItem.quantity + 1);
            if (qtyIncBtn.dataset.qty !== inc) qtyIncBtn.dataset.qty = inc;
          }

          const itemsWrap = document.querySelector('#fcd-drawer .fcd-items-wrap');
          if (itemsWrap && cartCache) {
            const newKeys = cartCache.items.map(i =>
              i.key + ':' + i.quantity + ':' + i.final_line_price
            ).join(',');
            itemsWrap.dataset.keys = newKeys;
          }

          if (itemsWrap) {
            const domCount = itemsWrap.querySelectorAll('.fcd-item').length;
            if (domCount > fresh.items.length) {
              if (typeof window.fcdRenderCart === 'function') {
                window.fcdRenderCart(fresh);
              }
            }
          }

          const newSelect = itemEl.querySelector('.csub-select');
          if (newSelect) {
            if (newSelect._changeHandler) {
              newSelect.removeEventListener('change', newSelect._changeHandler);
            }
            newSelect._changeHandler = async (e) => {
              const currentKey = wrapperEl.getAttribute('data-line-key');
              await updateSellingPlan(currentKey, e.target.value, newSelect, wrapperEl);
            };
            newSelect.addEventListener('change', newSelect._changeHandler);
            const newSelectValue = isClearingPlan ? '' : String(sellingPlanId);
            if (!isClearingPlan && updatedItem.selling_plan_allocation) {
              ensurePlanOption(newSelect, newSelectValue, updatedItem.selling_plan_allocation.selling_plan.name);
            }
            if (newSelect.value !== newSelectValue) newSelect.value = newSelectValue;
          }
        }
      } else if (typeof window.fcdRenderCart === 'function') {
        window.fcdRenderCart(fresh);
      }

      // Header sync
      const totalPrice = fresh.total_price;
      const count = fresh.items.reduce((sum, i) => sum + i.quantity, 0);

      const subtotalEl = document.querySelector('.fcd-subtotal__price');
      if (subtotalEl) {
        const newSub = '$' + (totalPrice / 100).toFixed(2);
        if (subtotalEl.textContent !== newSub) subtotalEl.textContent = newSub;
      }

      const checkoutEl = document.querySelector('.fcd-checkout-btn');
      if (checkoutEl) {
        const newCheckout = 'CHECKOUT • $' + (totalPrice / 100).toFixed(2);
        if (checkoutEl.textContent !== newCheckout) checkoutEl.textContent = newCheckout;
      }

      document.querySelectorAll('.header__cart-count, .cart-count-bubble span:first-child, [data-cart-count]').forEach(el => {
        const s = String(count);
        if (el.textContent !== s) el.textContent = s;
      });

      return { fresh, newKey: updatedItem ? updatedItem.key : null };
    } catch (err) {
      console.error('[CustomSub] Error updating selling plan:', err);
      syncSelectToCart(wrapperEl);
      return null;
    } finally {
      if (selectEl && selectEl.isConnected) {
        selectEl.disabled = false;
        selectEl.classList.remove('csub-select--loading');
      }
    }
  }

  async function updateSellingPlan(lineKey, sellingPlanId, selectEl, wrapperEl) {
    let lock = lineLocks.get(lineKey);
    if (!lock) {
      lock = { latestPlan: sellingPlanId, latestSelect: selectEl, latestWrapper: wrapperEl, queued: false };
      lineLocks.set(lineKey, lock);
    } else {
      lock.latestPlan = sellingPlanId;
      lock.latestSelect = selectEl;
      lock.latestWrapper = wrapperEl;
    }

    if (lock.queued) return;
    lock.queued = true;

    enqueueCartMutation(async () => {
      const finalLock = lineLocks.get(lineKey);
      if (!finalLock) return;
      const planToApply = finalLock.latestPlan;
      const selToUse = finalLock.latestSelect;
      const wrapToUse = finalLock.latestWrapper;
      finalLock.queued = false;

      const result = await performPlanChange(lineKey, planToApply, selToUse, wrapToUse);

      if (result && result.newKey && result.newKey !== lineKey) {
        const moved = lineLocks.get(lineKey);
        lineLocks.delete(lineKey);
        if (moved) lineLocks.set(result.newKey, moved);
      }

      const afterLock = lineLocks.get(result && result.newKey ? result.newKey : lineKey);
      if (afterLock && afterLock.latestPlan && afterLock.latestPlan !== planToApply && !afterLock.queued) {
        afterLock.queued = true;
        const nextKey = result && result.newKey ? result.newKey : lineKey;
        enqueueCartMutation(async () => {
          afterLock.queued = false;
          await performPlanChange(nextKey, afterLock.latestPlan, afterLock.latestSelect, afterLock.latestWrapper);
        });
      } else if (afterLock && !afterLock.queued) {
        const finalKey = result && result.newKey ? result.newKey : lineKey;
        const f = lineLocks.get(finalKey);
        if (f && !f.queued) lineLocks.delete(finalKey);
      }
    });
  }

  async function upgradeToSubscription(lineKey, wrapperEl) {
    wrapperEl.style.display = 'none';
    const selectorEl = buildSelector(lineKey, DEFAULT_SELLING_PLAN_ID, wrapperEl);
    wrapperEl.parentNode.insertBefore(selectorEl, wrapperEl.nextSibling);

    const select = selectorEl.querySelector('.csub-select');
    await updateSellingPlan(lineKey, DEFAULT_SELLING_PLAN_ID, select, selectorEl);
  }

  async function syncExistingProperties() {
    if (!cartCache) return;
    const subItems = cartCache.items.filter(i => i.selling_plan_allocation);
    if (!subItems.length) return;

    let needsSync = false;
    for (const item of subItems) {
      const planId = String(item.selling_plan_allocation.selling_plan.id);
      if (String(item.properties?._selling_plan_id) !== planId) { needsSync = true; break; }
    }
    if (!needsSync) return;

    for (const item of subItems) {
      const planId = String(item.selling_plan_allocation.selling_plan.id);
      if (String(item.properties?._selling_plan_id) !== planId) {
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              id: item.key,
              properties: { ...item.properties, _selling_plan_id: planId },
            }),
          });
        } catch (err) {
          console.error('[CustomSub] Failed to sync property for', item.title, err);
        }
      }
    }
    await fetchCart();
  }

  function buildSelector(lineKey, currentPlanId, parentWrapperEl, currentPlanName) {
    const wrapper = document.createElement('div');
    wrapper.className = 'csub-wrapper';
    wrapper.setAttribute('data-line-key', lineKey);

    const selectWrapper = document.createElement('div');
    selectWrapper.className = 'csub-select-wrapper';

    const select = document.createElement('select');
    select.className = 'csub-select';
    select.setAttribute('aria-label', 'Purchase option');

    // Group 1: One-time (group label is non-selectable; option clears the plan)
    const oneTimeGroup = document.createElement('optgroup');
    oneTimeGroup.label = 'One-time';

    const oneTimeOpt = document.createElement('option');
    oneTimeOpt.value = ''; // empty value clears selling_plan
    oneTimeOpt.textContent = 'One-time only';
    if (!currentPlanId) oneTimeOpt.selected = true;
    oneTimeGroup.appendChild(oneTimeOpt);

    select.appendChild(oneTimeGroup);

    // Group 2: Subscription (group label is non-selectable; option sets the plan)
    const subGroup = document.createElement('optgroup');
    subGroup.label = 'Subscription';
    subGroup.setAttribute('data-csub-group', 'subscription');

    SELLING_PLANS.forEach((plan) => {
      const opt = document.createElement('option');
      opt.value = plan.id;
      opt.textContent = plan.name;
      if (String(plan.id) === String(currentPlanId)) opt.selected = true;
      subGroup.appendChild(opt);
    });

    select.appendChild(subGroup);

    // Line has a plan we don't list yet — add it so it stays selected
    if (currentPlanId) {
      ensurePlanOption(select, currentPlanId, currentPlanName);
      select.value = String(currentPlanId);
    }

    select._changeHandler = async (e) => {
      const currentKey = wrapper.getAttribute('data-line-key');
      // Empty value = one-time (clears selling_plan); otherwise it's a plan id
      await updateSellingPlan(currentKey, e.target.value, select, wrapper);
    };
    select.addEventListener('change', select._changeHandler);

    selectWrapper.appendChild(select);
    wrapper.appendChild(selectWrapper);
    return wrapper;
  }

  function buildUpgradeButton(lineKey) {
    const wrapper = document.createElement('div');
    wrapper.className = 'csub-upgrade-wrapper';
    wrapper.setAttribute('data-upgrade-key', lineKey);

    const btn = document.createElement('button');
    btn.className = 'csub-upgrade-btn';
    btn.type = 'button';
    btn.textContent = 'Upgrade to Subscription & Save 15%';

    btn.addEventListener('click', () => upgradeToSubscription(lineKey, wrapper));

    wrapper.appendChild(btn);
    return wrapper;
  }

  async function injectSelectorsIntoCustomDrawer() {
    if (isInjectingCustomDrawer) return;
    isInjectingCustomDrawer = true;

    try {
      const drawer = document.getElementById('fcd-drawer');
      if (!drawer) return;
      if (!cartCache) return;

      const itemEls = drawer.querySelectorAll('.fcd-item');
      if (!itemEls.length) return;

      for (const itemEl of itemEls) {
        const key = itemEl.dataset.key;
        if (!key) continue;

        const cartItem = cartCache.items.find(i => i.key === key);
        if (!cartItem) continue;

        if (itemEl.dataset.csubPending === '1') continue;

        if (cartItem.selling_plan_allocation) {
          const planName = cartItem.selling_plan_allocation.selling_plan.name;
          ensureSubscriptionBadge(itemEl, planName);
        } else {
          removeSubscriptionBadge(itemEl);
        }

        const existingEl = itemEl.querySelector('.csub-wrapper, .csub-upgrade-wrapper');
        if (existingEl) {
          const existingKey = existingEl.getAttribute('data-line-key') || existingEl.getAttribute('data-upgrade-key');
          if (existingKey === key) {
            // Only sync the select if it's a dropdown (csub-wrapper, not the upgrade button)
            if (existingEl.classList.contains('csub-wrapper')) {
              const sel = existingEl.querySelector('.csub-select');
              if (sel && !sel.classList.contains('csub-select--loading')) {
                const targetValue = cartItem.selling_plan_allocation
                  ? String(cartItem.selling_plan_allocation.selling_plan.id)
                  : '';
                if (targetValue) {
                  ensurePlanOption(sel, targetValue, cartItem.selling_plan_allocation.selling_plan.name);
                }
                if (sel.value !== targetValue) sel.value = targetValue;
              }
            }
            continue;
          }
          existingEl.remove();
        }

        const infoEl = itemEl.querySelector('.fcd-item__info');
        if (!infoEl) continue;

        itemEl.dataset.csubPending = '1';

        try {
          if (cartItem.selling_plan_allocation) {
            const currentPlanId = cartItem.selling_plan_allocation.selling_plan.id;
            const currentPlanName = cartItem.selling_plan_allocation.selling_plan.name;
            if (!itemEl.querySelector('.csub-wrapper, .csub-upgrade-wrapper')) {
              infoEl.appendChild(buildSelector(cartItem.key, currentPlanId, null, currentPlanName));
            }
          } else {
            const hasPlans = await variantHasSellingPlans(cartItem.handle, cartItem.variant_id);
            if (!itemEl.isConnected) continue;
            if (hasPlans) {
              if (!itemEl.querySelector('.csub-wrapper, .csub-upgrade-wrapper')) {
                infoEl.appendChild(buildUpgradeButton(cartItem.key));
              }
            }
          }
        } finally {
          if (itemEl.isConnected) delete itemEl.dataset.csubPending;
        }
      }
    } finally {
      isInjectingCustomDrawer = false;
    }
  }

  window.fcdInjectSubscriptions = async function () {
    if (window._fcdCart) cartCache = window._fcdCart;
    if (!cartCache) await fetchCart();
    isInjectingCustomDrawer = false;
    const drawer = document.getElementById('fcd-drawer');
    if (drawer) {
      drawer.querySelectorAll('[data-csub-pending]').forEach(el => {
        delete el.dataset.csubPending;
      });
    }
    await injectSelectorsIntoCustomDrawer();
  };

  function injectStyles() {
    if (document.getElementById('csub-styles')) return;
    const style = document.createElement('style');
    style.id = 'csub-styles';
    // Honey amber palette: #EDB057 primary, #7a4f1d dark text,
    // #FBF1DD cream hover, #E5C896 pale border, amber rgba focus.
    style.textContent = `
      .csub-wrapper { display:flex; flex-direction:column; gap:5px; margin-top:10px; width:100%; }
      .csub-select-wrapper { position:relative; width:100%; }
      .csub-select-wrapper::after { content:''; position:absolute; right:11px; top:50%; transform:translateY(-50%); width:0; height:0; border-left:4px solid transparent; border-right:4px solid transparent; border-top:5px solid #7a4f1d; pointer-events:none; }
      .csub-select { width:100%; appearance:none; -webkit-appearance:none; background-color:#fff; background-image:none!important; border:1.5px solid #E5C896; border-radius:6px; padding:10px 30px 10px 14px; font-size:13px; font-weight:500; color:#7a4f1d; cursor:pointer; font-family:inherit; line-height:1.4; transition:border-color 0.18s,background-color 0.18s,box-shadow 0.18s; }
      .csub-select::-ms-expand { display:none; }
      .csub-select:hover { border-color:#EDB057; background-color:#FBF1DD; }
      .csub-select:focus { outline:none; border-color:#EDB057; background-color:#fff; box-shadow:0 0 0 3px rgba(237,176,87,0.25); }
      .csub-select--loading { opacity:0.55; cursor:wait; }
      .rebuy-cart__flyout-item-subscription .rebuy-button.outline,
      .rebuy-cart__flyout-item-subscription button.rebuy-button { display:none!important; }
      .csub-upgrade-wrapper { margin-top:10px; width:100%; }
      .csub-upgrade-btn { display:block; width:100%; background-color:#fff; border:1.5px solid #EDB057; border-radius:6px; padding:11px 14px; font-size:13px; font-weight:600; color:#7a4f1d; cursor:pointer; font-family:inherit; text-align:center; transition:background-color 0.18s,color 0.18s,border-color 0.18s; }
      .csub-upgrade-btn:hover { background-color:#EDB057; color:#fff; border-color:#EDB057; }
      .csub-upgrade-btn:disabled { opacity:0.55; cursor:wait; }
    `;
    document.head.appendChild(style);
  }

  function scheduleInject(delay) {
    delay = delay || 300;
    if (injectScheduled) return;
    injectScheduled = true;
    setTimeout(async () => {
      if (window._fcdCart) cartCache = window._fcdCart;
      else await fetchCart();
      await injectSelectorsIntoCustomDrawer();
      injectScheduled = false;
    }, delay);
  }

  function startObserver() {
    const observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((m) =>
        [...m.addedNodes].some((n) =>
          n.nodeType === 1 && (
            n.matches?.('.rebuy-cart__flyout-item-subscription') ||
            n.querySelector?.('.rebuy-cart__flyout-item-subscription')
          )
        )
      );
      if (relevant) scheduleInject(300);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function listenForUpdates() {
    document.addEventListener('rebuy:cart.change', () => scheduleInject(300));
    document.addEventListener('rebuy:smartcart.ready', () => scheduleInject(300));
  }

  async function init() {
    injectStyles();
    startObserver();
    listenForUpdates();
    await fetchCart();
    await syncExistingProperties();
    await injectSelectorsIntoCustomDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
